import { syncAccountPartner } from '../config/partnerThemes';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setError('');
    if (location.state && location.state.message) {
      setMessage(location.state.message);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const navigateToHomeApp = () => {
    const homeApp = localStorage.getItem('home_app');
    const routes = {
      emit:   '/emit-the-bible',
      sage:   '/emit-the-bible',
      champ:  '/mental-performance',
      ship:   '/mental-performance',
      safe:   '/safe',
      family: '/family-coach',
    };
    navigate(routes[homeApp] || '/select-home-app');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, timezone })
      });

      if (!response.ok) {
        let msg = null;
        try { const j = await response.json(); if (j && j.detail && j.detail.message) msg = j.detail.message; } catch (e2) { /* not JSON */ }
        const er = new Error(`HTTP error! Status: ${response.status}`);
        er.userMessage = msg;
        throw er;
      }

      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      // Use home_app from server (source of truth) — override localStorage
      if (data.home_app) {
        localStorage.setItem('home_app', data.home_app);
      } else {
        localStorage.removeItem('home_app');
      }
      await syncAccountPartner();
      navigateToHomeApp();

    } catch (err) {
      console.error('Login error:', err);
      setError(err.userMessage || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgMesh} />

      <div style={styles.card}>
        {/* Brand */}
        <div style={styles.brandRow}>
          {/* FHO Logo: Freedom (Blue) center dot, Happiness (Red) arc, Oneness (Green) outer circle */}
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Oneness — Green outer circle */}
            <circle cx="18" cy="18" r="16" stroke="#059669" strokeWidth="2.5" fill="none"/>
            {/* Happiness — Red 3/4 arc */}
            <path
              d="M18 8 A10 10 0 1 1 8 18"
              stroke="#ef4444"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
            {/* Freedom — Blue center dot */}
            <circle cx="18" cy="18" r="4" fill="#4f46e5"/>
          </svg>
          <span style={styles.brandName}>
            <span style={{color:'#4f46e5'}}>F</span>
            <span style={{color:'#ef4444'}}>H</span>
            <span style={{color:'#059669'}}>O</span>
            {' PLATFORM'}
          </span>
        </div>

        <h1 style={styles.heading}>Welcome back</h1>
        <p style={styles.subheading}>Sign in to continue your journey</p>

        {message && (
          <div style={{...styles.alert, ...styles.alertSuccess}}>{message}</div>
        )}
        {error && (
          <div style={{...styles.alert, ...styles.alertError}}>{error}</div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="you@example.com"
              required
              style={styles.input}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = 'rgba(30,58,95,0.2)'}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              required
              style={styles.input}
              onFocus={e => e.target.style.borderColor = '#4f46e5'}
              onBlur={e => e.target.style.borderColor = 'rgba(30,58,95,0.2)'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{...styles.btn, ...(loading ? styles.btnDisabled : {})}}
            onMouseEnter={e => { if (!loading) e.target.style.background = '#4338ca'; }}
            onMouseLeave={e => { if (!loading) e.target.style.background = '#4f46e5'; }}
          >
            {loading ? (
              <span style={styles.loadingRow}>
                <span style={styles.spinner} />
                Signing in...
              </span>
            ) : 'Sign In'}
          </button>
        </form>

        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>new here?</span>
          <span style={styles.dividerLine} />
        </div>

        <Link to="/register" style={styles.registerLink}>
          Create your account
        </Link>

        <p style={styles.tagline}>
          Wisdom · Mental wellness · Family coaching
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input::placeholder { color: rgba(30,58,95,0.35); }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    // Light sky-blue gradient matching the Emit app background
    background: '#5b9bd5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
  bgMesh: {
    position: 'absolute',
    inset: 0,
    background: `
      radial-gradient(ellipse 70% 50% at 15% 10%, rgba(255,255,255,0.35) 0%, transparent 55%),
      radial-gradient(ellipse 50% 40% at 85% 85%, rgba(91,155,213,0.3) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    width: '100%',
    maxWidth: '400px',
    background: 'rgba(255,255,255,0.75)',
    border: '1px solid rgba(255,255,255,0.9)',
    borderRadius: '20px',
    padding: '40px 36px',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 20px 60px rgba(30,58,95,0.18), 0 4px 16px rgba(30,58,95,0.1)',
    animation: 'fadeUp 0.45s ease forwards',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '28px',
  },
  brandName: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1e3a5f',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  heading: {
    margin: '0 0 6px',
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e3a5f',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  subheading: {
    margin: '0 0 28px',
    fontSize: '14px',
    color: 'rgba(30,58,95,0.6)',
  },
  alert: {
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '18px',
    lineHeight: 1.5,
  },
  alertSuccess: {
    background: 'rgba(5,150,105,0.1)',
    border: '1px solid rgba(5,150,105,0.3)',
    color: '#065f46',
  },
  alertError: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.25)',
    color: '#991b1b',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '18px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: {
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(30,58,95,0.6)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  input: {
    background: 'rgba(255,255,255,0.8)',
    border: '1px solid rgba(30,58,95,0.2)',
    borderRadius: '10px',
    padding: '12px 14px',
    fontSize: '15px',
    color: '#1e3a5f',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: "'Georgia', serif",
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    marginTop: '4px',
    background: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '13px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
    fontFamily: "'Georgia', serif",
    letterSpacing: '0.02em',
    width: '100%',
  },
  btnDisabled: {
    background: 'rgba(79,70,229,0.5)',
    cursor: 'not-allowed',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  spinner: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: '24px 0 16px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(30,58,95,0.12)',
  },
  dividerText: {
    fontSize: '11px',
    color: 'rgba(30,58,95,0.4)',
    letterSpacing: '0.06em',
  },
  registerLink: {
    display: 'block',
    textAlign: 'center',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid rgba(79,70,229,0.35)',
    color: '#4f46e5',
    fontSize: '14px',
    textDecoration: 'none',
    transition: 'border-color 0.2s, background 0.2s',
    fontFamily: "'Georgia', serif",
    fontWeight: '600',
  },
  tagline: {
    textAlign: 'center',
    fontSize: '11px',
    color: 'rgba(30,58,95,0.35)',
    marginTop: '28px',
    marginBottom: 0,
    letterSpacing: '0.04em',
  },
};

export default Login;
